import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import styles from 'pages/dashboards/v5/styles';
import { Text } from '@chakra-ui/react';
import { EditIcon, AddIcon, ViewIcon } from '@chakra-ui/icons';
import { Card, CardBody } from 'components/v4/Card';
import DashboardServiceCardSbcStatusbar from 'components/v4/Cards/DashboardServiceCard/DashboardServiceCardSbcStatusbar';
import ServiceStatus from 'pages/dashboards/v5/components/ServiceStatus';
import getSipTrunkCardStatus from 'pages/dashboards/v5/components/helpers/sipTrunkStatus';
import CardDetails from 'pages/dashboards/v5/components/CardDetails';
import DashboardCardEditButton from 'pages/dashboards/v5/components/DashboardCardEditButton';
import ServiceCardLayout, {
    ServiceCardDetail,
} from 'pages/dashboards/v5/components/ServiceCardLayout';

import trunkStatusCodeToText from 'utils/trunkStatusCodeToText';
import { MenuItem } from 'components/v4';

import useGetSRTTrunks from './queries/useGetSRTTrunks';
import useSRTTrunkTypes from 'hooks/useSRTTrunkTypes';

import { useDashboardMenu } from 'context/DashboardMenuContext';

export default function SRTCard({ service }) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { darkMode } = useSelector((state) => state.settings);
    const { isQueryDisabled } = useDashboardMenu();
    const currentCompany = useSelector(
        (state) => state.navigationLists.currentCompany,
    );
    const accountScope = useSelector((state) => state.login.requiredScope);

    // SRT trunks are matched by trunk type id — the display name is
    // backend-owned and must not be relied on for filtering.
    const { isSRTTrunk } = useSRTTrunkTypes();

    const { data: trunkData } = useGetSRTTrunks({
        companyID: currentCompany,
        enabled: !isQueryDisabled,
    });

    const [cardStatus, setCardStatus] = useState({
        trunkCount: '...',
        channelCount: '...',
        status: '...',
        statusCode: null,
    });

    const calculateSrtStatus = useCallback(
        (data) => {
            const srtTrunks = (data?.data || []).filter(isSRTTrunk);

            let trunkCount = 0;
            let channelCount = 0;
            let statusCode = null;
            let status = '';

            srtTrunks.forEach((trunk) => {
                trunkCount++;
                channelCount += trunk.channels;
            });

            const isError = srtTrunks
                .map((t) => (t.status === 20 ? t.status : undefined))
                .filter((n) => n !== undefined);
            const isWarning = srtTrunks
                .map((t) =>
                    t.status === 8 || t.status === 9 ? t.status : undefined,
                )
                .filter((n) => n !== undefined);
            const isOk = srtTrunks
                .map((t) =>
                    t.status >= 0 && t.status <= 4 ? t.status : undefined,
                )
                .filter((n) => n !== undefined);

            if (isError.length) {
                statusCode = Math.max(...isError);
                status = trunkStatusCodeToText(statusCode);
            } else if (isWarning.length) {
                statusCode = Math.max(...isWarning);
                status = trunkStatusCodeToText(statusCode);
            } else if (isOk.length) {
                statusCode = Math.max(...isOk);
                status = trunkStatusCodeToText(statusCode);
            }

            setCardStatus({ trunkCount, channelCount, status, statusCode });
        },
        [isSRTTrunk],
    );

    useEffect(() => {
        if (trunkData) {
            setTimeout(() => {
                calculateSrtStatus(trunkData);
            }, 500);
        }
    }, [trunkData, calculateSrtStatus]);

    const openResourcesMenu = () => {
        const tel = document.querySelector('.Resources');
        if (tel?.children.length === 1) {
            tel.querySelector('div')?.dispatchEvent(
                new MouseEvent('click', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                }),
            );
        }
    };

    const addTrunks = () => {
        openResourcesMenu();
        dispatch({
            type: 'REDIRECT',
            payload: { pathname: '/resources/trunks', scope: 20 },
        });
        navigate('/trunks/add/srt');
    };

    const viewTrunks = () => {
        openResourcesMenu();
        dispatch({
            type: 'REDIRECT',
            payload: { pathname: '/resources/trunks', scope: 20 },
        });
        navigate('/trunks');
    };

    // (3134) Degraded and Error name the trunk status rather than leaving
    // the pill blank; the collapsed header's badge reads the same.
    const pillStatus = getSipTrunkCardStatus(
        cardStatus.statusCode ?? service?.statusDisplay?.statusDisplayLevel,
    );

    return (
        <Card
            bg={darkMode ? 'dark.tcap' : null}
            sx={{
                ...styles.serviceCard,
                border: '1px solid',
                borderColor: darkMode ? 'gray.700' : 'gray.200',
                boxShadow: darkMode
                    ? 'none'
                    : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            }}>
            <DashboardServiceCardSbcStatusbar
                sx={styles.verticalBar}
                status={
                    cardStatus.statusCode ??
                    service?.statusDisplay?.statusDisplayLevel
                }
            />
            <CardBody
                sx={{
                    display: 'flex',
                    flexDir: 'column',
                    ...styles.serviceCardBody,
                }}>
                <ServiceCardLayout
                    icon={CardDetails(service?.serviceType)?.icon}
                    name={CardDetails(service?.serviceType)?.serviceName}
                    status={
                        <ServiceStatus
                            service={service}
                            cardStatus={{
                                ...cardStatus,
                                ...pillStatus,
                            }}
                        />
                    }>
                    {/* Trunks */}
                    <ServiceCardDetail label="Trunks:">
                        {cardStatus.trunkCount}
                    </ServiceCardDetail>
                    {/* Channels */}
                    <ServiceCardDetail label="Channels:">
                        {cardStatus.channelCount}
                    </ServiceCardDetail>
                </ServiceCardLayout>
                <DashboardCardEditButton>
                    <MenuItem
                        icon={<AddIcon />}
                        onClick={addTrunks}
                        data-walkthroughid={`/dashboard-service/card/${service.name}/add-trunk`}>
                        <Text marginBottom="0">Add Trunk</Text>
                    </MenuItem>
                    <MenuItem icon={<ViewIcon />} onClick={viewTrunks}>
                        <Text marginBottom="0">View Trunks</Text>
                    </MenuItem>
                    {accountScope >= 40 && (
                        <Link
                            className="bg-transparent !no-underline hover:text-inherit"
                            to={`/services/srt/${service.id}/edit`}
                            state={{ serviceId: service.id, data: service }}>
                            <MenuItem
                                icon={<EditIcon />}
                                data-walkthroughid={`/dashboard-service/card/${service.name}/edit-service`}>
                                <Text marginBottom="0">Edit Service</Text>
                            </MenuItem>
                        </Link>
                    )}
                </DashboardCardEditButton>
            </CardBody>
        </Card>
    );
}
